import { ChangeEvent } from 'react';
import { Controller, FieldValues, UseFormSetValue, useFormState } from 'react-hook-form';
import CalendarIcon from '@untitled-ui/icons-react/build/esm/Calendar';
import PercentIcon from '@untitled-ui/icons-react/build/esm/Percent02';

import { DatePicker, Input, Select } from 'antd';
import AddressInput, { Address } from 'components/Inputs/AddressInput';
import FieldError from 'components/Inputs/FieldError';
import MobilePhoneInput from 'components/Inputs/MobilePhoneInput';
import { endOfToday, isAfter, isBefore } from 'date-fns';
import { mobilePhoneRules, visibleMobilePhoneError } from 'utils';

import './AddTrustIndividualForm.scss';

const { Option } = Select;

const disabledDate = (current: any) => {
  const minDate = new Date(1900, 0, 1);
  return (
    current && (isAfter(current.toDate(), endOfToday()) || isBefore(current.toDate(), minDate))
  );
};

const formFields = [
  {
    name: 'firstName',
    label: 'First Name',
    component: Input,
    rules: { required: 'First Name is required' },
  },
  {
    name: 'middleName',
    label: 'Middle Name',
    component: Input,
  },
  {
    name: 'lastName',
    label: 'Last Name',
    component: Input,
    rules: { required: 'Last Name is required' },
  },
  {
    name: 'dateOfBirth',
    label: 'Date of Birth',
    component: DatePicker,
    placeholder: '',
    suffixIcon: (
      <div className="border-l border-gray-300 h-10 flex items-center mt-0.5">
        <CalendarIcon className="text-gray-700 h-5 w-5 ml-2.5" />
      </div>
    ),
    rules: { required: 'Field required' },
    disabledDate: disabledDate,
  },
  {
    name: 'email',
    label: 'Email',
    component: Input,
    rules: {
      required: 'Email is required',
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email address',
      },
    },
  },
  {
    name: 'mobilePhone',
    label: 'Mobile Phone',
    component: MobilePhoneInput,
    rules: mobilePhoneRules,
    className: 'w-[302px]',
  },
  {
    name: 'address',
    label: 'Address',
    component: AddressInput,
    maxLength: 50,
    rules: {
      required: 'Address is required',
    },
    className: 'min-w-[620px]',
  },
  {
    name: 'ownership',
    label: 'Ownership',
    component: Input,
    rules: { required: 'Ownership is required' },
    prefix: <PercentIcon className="text-gray-700 h-4 w-4" />,
    type: 'text',
  },
  {
    name: 'role',
    label: 'Role',
    component: Select,
    rules: { required: 'Role is required' },
    isSelect: true,
    placeholder: <div className="text-gray-900 font-semibold">Select</div>,
  },
];

interface IndividualFormFieldsProps {
  control: any;
  errors: any;
  roleOptions?: any;
  formIndex?: number;
  setValue: UseFormSetValue<FieldValues>;
}

export const IndividualFormFields = ({
  control,
  errors,
  roleOptions,
  formIndex = 1,
  setValue,
}: IndividualFormFieldsProps) => {
  const { isSubmitted, touchedFields } = useFormState({ control });

  return (
    <div className="flex flex-wrap items-start justify-start w-full gap-5 bg-gray-100 p-[14px] rounded-lg mt-4">
      {formFields?.map((field) => {
        const isRequired = field.rules?.required;
        const fieldName = `${field.name}_${formIndex}`;
        const fieldError =
          field.name === 'mobilePhone'
            ? visibleMobilePhoneError(errors[fieldName], !!touchedFields[fieldName], isSubmitted)
            : errors[fieldName];

        return (
          <div key={fieldName}>
            <label className="block mb-1 text-gray-600 font-semibold text-xs">
              {field.label} {isRequired && '*'}
            </label>

            <Controller
              name={fieldName}
              control={control}
              rules={field.rules}
              render={({ field: controllerField }) => {
                const Component: React.ElementType = field.isSelect ? Select : field.component;
                if (field.name === 'address') {
                  return (
                    <AddressInput
                      control={control}
                      name={fieldName}
                      className={`h-10 ${field.className || ''} font-medium ${
                        fieldError ? 'border border-red-500' : ''
                      }`}
                      onChange={(value: Address) => setValue(fieldName, value || '')}
                    />
                  );
                }
                if (field.name === 'mobilePhone') {
                  return (
                    // controllerField.onChange rather than setValue, so the number is
                    // revalidated as it is typed and a stale error clears itself.
                    <MobilePhoneInput
                      {...controllerField}
                      className={field.className}
                      status={fieldError ? 'error' : ''}
                    />
                  );
                }

                const ownershipStyle =
                  field.name === 'ownership' || field.name === 'role'
                    ? 'min-w-[302px]'
                    : 'min-w-[194px]';

                return (
                  <Component
                    {...controllerField}
                    prefix={field.prefix}
                    type={field.type}
                    placeholder={field.placeholder}
                    suffixicon={field.suffixIcon}
                    className={`h-10 ${ownershipStyle} text-gray-900 font-medium`}
                    status={fieldError ? 'error' : ''}
                    format="DD/MM/YYYY"
                    disabledDate={field.disabledDate}
                    rules={field.rules}
                    onChange={(e: ChangeEvent<HTMLInputElement> | any) => {
                      if (field.name === 'dateOfBirth') {
                        setValue(fieldName, e || null);
                      } else if (field.name === 'role') {
                        setValue(fieldName, e);
                      } else {
                        setValue(fieldName, e.target?.value || '');
                      }
                    }}
                  >
                    {field.isSelect &&
                      roleOptions.map((role: any) => (
                        <Option key={role.value} value={role.value}>
                          {role.label}
                        </Option>
                      ))}
                  </Component>
                );
              }}
            />
            <FieldError message={fieldError?.message} />
          </div>
        );
      })}
    </div>
  );
};
